import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import RichTextEditor from "@/components/RichTextEditor";
import { useAuth } from "@/contexts/AuthContext";
import { isAdminLike } from "@/lib/roles";
import api, { fetchBlobUrl } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate as fmtDateSafe } from "@/lib/dates";
import {
  ArrowLeft, Pencil, Save, X, Trash2, Star, Pin, Upload, Image as ImageIcon,
  Calendar, User as UserIcon, ImageMinus,
} from "lucide-react";

const BRANCHES = [
  { key: "all", label: "Todo el Club" },
  { key: "tackle", label: "Tackle" },
  { key: "flag_masculino", label: "Flag Masculino" },
  { key: "flag_femenino", label: "Flag Femenino" },
];
const BRANCH_LABEL = Object.fromEntries(BRANCHES.map(b => [b.key, b.label]));

const fmtDate = (iso) => fmtDateSafe(iso, { year: "numeric", month: "long", day: "numeric" }, "");

export default function FerroPostDetail() {
  const { sectionId, postId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = isAdminLike(user?.role);
  const isNew = postId === "new";

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [editing, setEditing] = useState(isNew);
  const [draft, setDraft] = useState({
    title: "", summary: "", content_html: "", event_date: "", branch: "all",
    is_featured: false, is_pinned: false, status: "draft",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isNew) return;
    let alive = true;
    setLoading(true);
    api.get(`/ferro/posts/${postId}`)
      .then(r => { if (!alive) return; setPost(r.data); setDraft(fromPost(r.data)); })
      .catch(() => toast.error("Publicación no encontrada"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [postId, isNew]);

  if (!["mural", "logros", "tradiciones"].includes(sectionId)) return <Navigate to="/somos-ferro" replace/>;
  if (isNew && !isAdmin) return <Navigate to={`/somos-ferro/${sectionId}`} replace/>;

  const save = async (targetStatus) => {
    setSaving(true);
    try {
      const payload = {
        title: draft.title.trim(),
        summary: draft.summary.trim() || null,
        content_html: draft.content_html,
        event_date: draft.event_date || null,
        branch: draft.branch,
        is_featured: draft.is_featured,
        is_pinned: draft.is_pinned,
      };
      let saved;
      if (isNew || !post) {
        payload.status = targetStatus || "draft";
        const r = await api.post(`/ferro/sections/${sectionId}/posts`, payload);
        saved = r.data;
        toast.success(targetStatus === "published" ? "Publicada" : "Guardada como borrador");
        // Actualiza estado local y sale del modo edición para que el usuario
        // vea la vista de lectura del post recién creado.
        setPost(saved);
        setDraft(fromPost(saved));
        setEditing(false);
        nav(`/somos-ferro/${sectionId}/${saved.id}`, { replace: true });
        return;
      }
      const r = await api.patch(`/ferro/posts/${post.id}`, payload);
      saved = r.data;
      if (targetStatus === "published" && saved.status !== "published") {
        const p2 = await api.post(`/ferro/posts/${saved.id}/publish`);
        saved = p2.data;
        toast.success("Publicada");
      } else if (targetStatus === "unpublished") {
        const p2 = await api.post(`/ferro/posts/${saved.id}/unpublish`);
        saved = p2.data;
        toast.success("Despublicada");
      } else {
        toast.success("Guardado");
      }
      setPost(saved); setDraft(fromPost(saved)); setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const doTrash = async () => {
    if (!window.confirm(`¿Enviar "${post.title}" a la papelera?`)) return;
    try {
      await api.delete(`/ferro/posts/${post.id}`);
      toast.success("En papelera");
      nav(`/somos-ferro/${sectionId}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const uploadImage = async (file) => {
    if (!post) { toast.error("Guarda el borrador antes de subir imágenes"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/ferro/posts/${post.id}/images`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.get(`/ferro/posts/${post.id}`);
      setPost(r.data); setDraft(fromPost(r.data));
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir");
    } finally { setUploading(false); }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("¿Eliminar esta imagen?")) return;
    try {
      await api.delete(`/ferro/posts/${post.id}/images/${imageId}`);
      const r = await api.get(`/ferro/posts/${post.id}`);
      setPost(r.data); setDraft(fromPost(r.data));
    } catch (err) { toast.error("Error"); }
  };

  const setCover = async (imageId) => {
    try {
      const r = await api.patch(`/ferro/posts/${post.id}`, { cover_image_id: imageId });
      setPost(r.data); setDraft(fromPost(r.data));
      toast.success("Portada actualizada");
    } catch { toast.error("Error"); }
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[900px]" data-testid={`ferro-post-${postId}`}>
        <Link to={`/somos-ferro/${sectionId}`} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#A1A1AA] hover:text-cyan-400 mb-6" data-testid="post-back">
          <ArrowLeft size={14}/> Volver
        </Link>

        {loading && <div className="tactical-card p-10 text-center text-[#71717A]">Cargando…</div>}

        {!loading && !editing && post && (
          <>
            <PostHeader post={post}/>
            <PostCover post={post}/>
            <div className="tactical-card p-6 mt-4 prose-tactical" data-testid="post-content"
                 dangerouslySetInnerHTML={{ __html: post.content_html || "<p><em>Sin contenido.</em></p>" }}/>
            {post.images?.length > 0 && <Gallery images={post.images} isAdmin={isAdmin} onDelete={deleteImage} onSetCover={setCover} activeCoverId={post.cover_image_id}/>}
            {isAdmin && (
              <div className="flex gap-2 mt-6 flex-wrap" data-testid="post-admin-actions">
                <button onClick={() => setEditing(true)} className="btn-ghost text-xs flex items-center gap-1"><Pencil size={12}/> Editar</button>
                {post.status === "published"
                  ? <button onClick={() => save("unpublished")} className="btn-ghost text-xs">Despublicar</button>
                  : <button onClick={() => save("published")} className="btn-primary text-xs">Publicar</button>}
                <button onClick={doTrash} className="btn-ghost text-xs text-[#FF3B30] border-[#FF3B30] flex items-center gap-1"><Trash2 size={12}/> Eliminar</button>
              </div>
            )}
          </>
        )}

        {!loading && editing && (
          <EditorForm
            draft={draft} setDraft={setDraft} post={post} saving={saving} uploading={uploading}
            onSaveDraft={() => save("draft")}
            onPublish={() => save("published")}
            onCancel={() => { if (isNew) nav(`/somos-ferro/${sectionId}`); else { setEditing(false); setDraft(fromPost(post)); } }}
            onUploadImage={uploadImage} onDeleteImage={deleteImage} onSetCover={setCover}
          />
        )}
        <div className="h-20 md:h-6"/>
      </div>
    </Layout>
  );
}

function fromPost(p) {
  return {
    title: p.title || "", summary: p.summary || "", content_html: p.content_html || "",
    event_date: p.event_date || "", branch: p.branch || "all",
    is_featured: !!p.is_featured, is_pinned: !!p.is_pinned, status: p.status,
  };
}

function PostHeader({ post }) {
  return (
    <>
      <div className="flex items-center gap-1.5 flex-wrap mb-2">
        {post.is_pinned && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-cyan-400 text-cyan-400 flex items-center gap-1"><Pin size={10}/>Fijado</span>}
        {post.is_featured && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[#FACC15] text-[#FACC15] flex items-center gap-1"><Star size={10}/>Destacado</span>}
        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-[#27272A] text-[#A1A1AA]">{BRANCH_LABEL[post.branch] || post.branch}</span>
      </div>
      <h1 className="font-display text-3xl sm:text-4xl text-white leading-tight" data-testid="post-title">{post.title}</h1>
      {post.summary && <p className="text-[#A1A1AA] mt-2 text-lg">{post.summary}</p>}
      <div className="flex items-center gap-4 mt-3 text-[10px] uppercase tracking-widest text-[#71717A]">
        <span className="flex items-center gap-1"><Calendar size={11}/>{fmtDate(post.event_date || post.published_at || post.created_at)}</span>
        {post.author_name && <span className="flex items-center gap-1"><UserIcon size={11}/>{post.author_name}</span>}
      </div>
    </>
  );
}

function PostCover({ post }) {
  const coverImg = useMemo(() => post.images?.find(i => i.id === post.cover_image_id) || post.images?.[0], [post]);
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!coverImg) { setUrl(null); return; }
    let alive = true;
    fetchBlobUrl(`/ferro/images/${coverImg.storage_path}`).then(u => { if (alive) setUrl(u); });
    return () => { alive = false; };
  }, [coverImg]);
  if (!coverImg) return null;
  return (
    <div className="mt-4 aspect-[16/9] bg-[#0A0A0A] border border-[#27272A] overflow-hidden">
      {url ? <img src={url} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full animate-pulse bg-[#111]"/>}
    </div>
  );
}

function Gallery({ images, isAdmin, onDelete, onSetCover, activeCoverId }) {
  return (
    <div className="mt-6" data-testid="post-gallery">
      <div className="overline text-[#71717A] mb-2">Galería</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {images.map(img => <GalleryImg key={img.id} img={img} isAdmin={isAdmin} onDelete={onDelete} onSetCover={onSetCover} isCover={img.id === activeCoverId}/>)}
      </div>
    </div>
  );
}

function GalleryImg({ img, isAdmin, onDelete, onSetCover, isCover }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchBlobUrl(`/ferro/images/${img.storage_path}`).then(u => { if (alive) setUrl(u); });
    return () => { alive = false; };
  }, [img]);
  return (
    <div className={`relative aspect-square bg-[#0A0A0A] border overflow-hidden ${isCover ? "border-cyan-400" : "border-[#27272A]"}`}>
      {url ? <img src={url} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full animate-pulse bg-[#111]"/>}
      {isAdmin && (
        <div className="absolute inset-x-0 bottom-0 flex gap-1 p-1 bg-black/60">
          <button onClick={() => onSetCover(img.id)} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-cyan-400 text-cyan-400 flex-1" title="Usar como portada">{isCover ? "Portada" : "Cover"}</button>
          <button onClick={() => onDelete(img.id)} className="text-[9px] px-1 border border-[#FF3B30] text-[#FF3B30]" title="Eliminar"><ImageMinus size={10}/></button>
        </div>
      )}
    </div>
  );
}

function EditorForm({ draft, setDraft, post, saving, uploading, onSaveDraft, onPublish, onCancel, onUploadImage, onDeleteImage, onSetCover }) {
  const handleFile = (e) => {
    const f = e.target.files?.[0]; if (f) onUploadImage(f); e.target.value = "";
  };
  return (
    <div className="tactical-card p-6 space-y-4" data-testid="post-editor">
      <label className="block">
        <span className="overline block mb-1">Título *</span>
        <input required className="input-tactical" maxLength={200} value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} data-testid="editor-title"/>
      </label>
      <label className="block">
        <span className="overline block mb-1">Resumen / bajada</span>
        <input className="input-tactical" maxLength={400} value={draft.summary} onChange={e => setDraft({ ...draft, summary: e.target.value })} data-testid="editor-summary"/>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="block">
          <span className="overline block mb-1">Fecha</span>
          <input type="date" className="input-tactical" value={draft.event_date || ""} onChange={e => setDraft({ ...draft, event_date: e.target.value })}/>
        </label>
        <label className="block">
          <span className="overline block mb-1">Rama</span>
          <select className="input-tactical" value={draft.branch} onChange={e => setDraft({ ...draft, branch: e.target.value })} data-testid="editor-branch">
            {BRANCHES.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </label>
        <div className="flex items-end gap-4 pb-1">
          <label className="flex items-center gap-2"><input type="checkbox" checked={draft.is_pinned} onChange={e => setDraft({ ...draft, is_pinned: e.target.checked })}/><span className="text-sm text-white">Fijar arriba</span></label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={draft.is_featured} onChange={e => setDraft({ ...draft, is_featured: e.target.checked })}/><span className="text-sm text-white">Destacada</span></label>
        </div>
      </div>
      <div>
        <span className="overline block mb-1">Contenido</span>
        <RichTextEditor value={draft.content_html} onChange={html => setDraft({ ...draft, content_html: html })} testid="editor-content"/>
      </div>
      {post && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="overline">Imágenes ({post.images?.length || 0}/10)</span>
            <label className={`btn-ghost text-xs flex items-center gap-1 cursor-pointer ${uploading ? "opacity-50" : ""}`}>
              <Upload size={12}/> {uploading ? "Subiendo…" : "Subir imagen"}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} disabled={uploading} data-testid="editor-upload"/>
            </label>
          </div>
          {post.images?.length > 0
            ? <Gallery images={post.images} isAdmin={true} onDelete={onDeleteImage} onSetCover={onSetCover} activeCoverId={post.cover_image_id}/>
            : <div className="text-xs text-[#71717A] flex items-center gap-1"><ImageIcon size={12}/> Aún sin imágenes. Formatos: JPG, PNG, WEBP · Máx 5 MB · Hasta 10 imágenes.</div>}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-3 border-t border-[#27272A] flex-wrap">
        <button onClick={onCancel} disabled={saving} className="btn-ghost text-xs flex items-center gap-1"><X size={12}/> Cancelar</button>
        <button onClick={onSaveDraft} disabled={saving || !draft.title.trim()} className="btn-ghost text-xs flex items-center gap-1" data-testid="save-draft"><Save size={12}/> Guardar borrador</button>
        <button onClick={onPublish} disabled={saving || !draft.title.trim()} className="btn-primary text-xs flex items-center gap-1" data-testid="publish-post"><Save size={12}/> {saving ? "…" : "Publicar"}</button>
      </div>
    </div>
  );
}
